/**
 * Which packages each market offers, and the catalogue visibility that follows.
 *
 * A biomarker or health risk carries a global list of package tags (`pro`,
 * `addon-trace-elements`, …). Whether a package is sold in a market is CMS
 * data: the `products` collection gives every product a `packageTag` and a
 * per-country `marketStatus`. This module joins the two, so a catalogue entry
 * is shown in a market only when at least one of its packages is offered
 * there — available, coming soon or on a waitlist. A health risk whose only
 * package is the Swiss trace-elements add-on no longer appears on /de or /nl.
 *
 * This is one of two layers. The editor's `hideInCountries` still hides a
 * document outright, and stays the only way to express that the SAME package
 * measures different markers per market (NL dropped four hemolysis-sensitive
 * markers its lab cannot run, AHD-12853). An entry is visible when neither
 * layer hides it; the loaders merge this layer's result into
 * `hideInCountries`, so every `visibleInCountry` / `isHiddenInCountry`
 * consumer applies both.
 *
 * Pure and client-safe: the explorer runs the same join in the browser.
 */

import type { Country } from "@/i18n/config"
import type { ServiceMarketStatus } from "@/lib/cms"

const MARKETS: readonly Country[] = ["ch", "nl", "de", "at"]

/** A product in one of these states is on offer; `hidden` and `not_available`
 *  are not. */
const OFFERED_STATUSES = new Set<ServiceMarketStatus>([
  "available",
  "coming_soon",
  "waitlist",
])

/** The `products` fields the join reads. */
export interface PackageOfferingProduct {
  packageTag?: string | null
  marketStatus?: { country: string; status: ServiceMarketStatus }[] | null
}

/** Package tag → the markets where at least one product with that tag is on
 *  offer. A tag that is absent has no product at all. */
export type PackageOffering = Record<string, Country[]>

/**
 * Build the tag → markets map from the published products. A market with no
 * `marketStatus` entry is `available`, as everywhere else that reads the
 * field (`statusForCountry`). Several products can share a tag (the three
 * single-marker add-ons share `addon-single`); the tag is offered where any
 * of them is.
 */
export function buildPackageOffering(
  products: readonly PackageOfferingProduct[]
): PackageOffering {
  const offering: PackageOffering = {}
  for (const product of products) {
    const tag = product.packageTag
    if (!tag) continue
    const offeredHere = MARKETS.filter((market) => {
      const entry = product.marketStatus?.find(
        (m) => m.country?.toLowerCase() === market
      )
      return OFFERED_STATUSES.has(entry?.status ?? "available")
    })
    offering[tag] = Array.from(
      new Set([...(offering[tag] ?? []), ...offeredHere])
    )
  }
  return offering
}

/** Package tags of a market's own base blood check-up, sold in place of the
 *  Advanced blood test's `blood` panel. */
const BASE_BLOOD_STAND_IN_TAGS = [
  "prevention-starter",
  "advanced-blood-test-nl",
]

/**
 * Whether a package tag is on offer in a market.
 *
 * A tag with no product behind it (`blood-followup`, the membership follow-up
 * test) is treated as offered: with nothing to read a status from, hiding
 * would be a guess. `blood` also counts where the market's own base blood
 * check-up is sold instead: Germany sells the Vorsorge-Starter
 * (`prevention-starter`) in place of the Advanced blood test, the Netherlands
 * the reduced `advanced-blood-test-nl` panel under the Advanced blood test's
 * name (AHD-13560), and health risks cannot carry either tag.
 */
export function isPackageOffered(
  tag: string,
  country: string,
  offering: PackageOffering
): boolean {
  const market = country.toLowerCase() as Country
  const markets = offering[tag]
  if (!markets) return true
  if (markets.includes(market)) return true
  return (
    tag === "blood" &&
    BASE_BLOOD_STAND_IN_TAGS.some((t) => Boolean(offering[t]?.includes(market)))
  )
}

/** The markets where none of an entry's packages is on offer. An entry with no
 *  packages is left alone. */
export function marketsNotOffering(
  packages: readonly string[],
  offering: PackageOffering
): Country[] {
  if (packages.length === 0) return []
  return MARKETS.filter(
    (market) => !packages.some((tag) => isPackageOffered(tag, market, offering))
  )
}

/**
 * Merge the markets that offer none of an entry's packages into its
 * `hideInCountries`, next to whatever the editor hid. `offering` is `null`
 * when the products could not be read; the catalogue is then left as it is
 * rather than emptied.
 *
 * Must run on the CMS's own package tags, before `withSitePackageIds` folds
 * them: the Swiss `addon-bvitamins-homocysteine` is hidden outside CH, and
 * folded into `addon-bvitamins` it would read as offered everywhere.
 */
export function withMarketOffering<
  T extends { packages: string[]; hideInCountries?: Country[] | null },
>(docs: T[], offering: PackageOffering | null): T[] {
  if (!offering) return docs
  return docs.map((doc) => {
    if (!Array.isArray(doc.packages)) return doc
    const notOffered = marketsNotOffering(doc.packages, offering)
    if (notOffered.length === 0) return doc
    return {
      ...doc,
      hideInCountries: Array.from(
        new Set([...(doc.hideInCountries ?? []), ...notOffered])
      ),
    }
  })
}
